// src/Pages/UserManagement.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api.js';
import UserForm from '../Components/UserForm.js';
import UserList from '../Components/UserList.js';
import PaginationControls from '../Components/PaginationControls.js';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';
import styles from './UserManagement.module.css';

// Estado inicial del formulario para mantener la limpieza
const INITIAL_FORM_STATE = {
  id: null,
  name: '',
  last_name: '',
  mail: '',
  oldPassword: '',
  password: '',
  role: 'client',
  tel_number: '',
  identification: '',
  birthdate: ''
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false); // 💡 Nuevo estado de carga
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // --- Data Fetching ---

  const fetchUsers = useCallback(async (currentPage) => {
    setIsLoading(true); // Muestra spinner o indicador
    try {
      const res = await api.get('/user', {
        params: { page: currentPage, limit: 10 }
      });

      setUsers(res.data.users);
      setPage(res.data.page);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      showToast('Error al cargar usuarios. Verifica la consola.', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUsers(page);
  }, [page, fetchUsers]); // Re-fetch solo al cambiar la página

  // --- Mutaciones y Handlers ---

  // 💡 Estrategia Senior: Actualizar el estado local después de una operación exitosa
  const handleUpdateUserList = (updatedUser) => {
    setUsers(prevUsers => {
      if (updatedUser.id) {
        // Verificar si el usuario ya existe en la lista (edición) o es nuevo (creación)
        const exists = prevUsers.some(u => (u.ID || u.id) === updatedUser.id);
        if (exists) {
          return prevUsers.map(user => {
            const userId = user.ID || user.id;
            return userId === updatedUser.id ? { ...user, ...updatedUser } : user;
          });
        }
      }
      // Creación — agregar al inicio
      return [updatedUser, ...prevUsers].slice(0, 10);
    });
  };

  const handleDeleteUser = useCallback(async (id) => {
    const ok = await confirm('¿Desactivar este usuario? Esta acción es reversible.', {
      title: 'Confirmar desactivación',
      confirmText: 'Desactivar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      // DELETE /user/:id
      await api.delete(`/user/${id}`);

      // Actualización local inmediata
      setUsers(prevUsers => prevUsers.filter(user => user.id !== id));
      if (form.id === id) setForm(INITIAL_FORM_STATE);

      // 💡 Mejor práctica: Re-fetch de la página actual para asegurar la paginación correcta
      fetchUsers(page);
      showToast('Usuario desactivado correctamente.', { type: 'success' });
    } catch (err) {
      showToast('No se pudo desactivar el usuario. Intenta de nuevo.', { type: 'error' });
    }
  }, [form.id, fetchUsers, page, confirm, showToast]);


  // Trae el perfil completo del usuario antes de editar
  // (la lista paginada no devuelve Identification ni Birthdate)
  const formRef = useRef(null);

  const handleEdit = useCallback(async (user) => {
    const userId = user.ID || user.id;
    try {
      const res = await api.get(`/user/${userId}`);
      const u = res.data;
      setForm({
        id: userId,
        name:           u.Name           || u.name           || '',
        last_name:      u.Last_name      || u.last_name      || '',
        mail:           u.Email          || u.mail           || '',
        oldPassword:    '',
        password:       '',
        role:           u.Rol            || u.role           || 'client',
        tel_number:     u.Number_phone   || u.tel_number     || '',
        identification: u.Identification || u.identification || '',
        birthdate:      u.Birthdate      ? u.Birthdate.split('T')[0]
                      : u.birthdate      ? u.birthdate.split('T')[0]
                      : '',
      });
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (err) {
      showToast('No se pudo cargar la información completa del usuario.', { type: 'error' });
    }
  }, [showToast]);

  // --- Renderizado ---

  return (
    <div className={styles.container}>
      <h2>Gestión de Usuarios</h2>

      {/* 1. Formulario (Delegación de UI y Submit) */}
      <div ref={formRef}>
        <UserForm
          form={form}
          setForm={setForm}
          resetForm={() => setForm(INITIAL_FORM_STATE)}
          onSuccess={handleUpdateUserList}
        />
      </div>
      
      <h3 className={styles.listTitle}>Lista de Usuarios</h3>
      
      {/* Lista siempre visible — solo se opaca mientras carga para evitar el flash */}
      <div style={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.15s', pointerEvents: isLoading ? 'none' : 'auto' }}>
        <UserList
          users={users}
          onEdit={handleEdit}
          onDelete={handleDeleteUser}
        />
      </div>

      {totalPages > 1 && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          setPage={setPage}
        />
      )}
    </div>
  );
};

export default UserManagement;

